import { connect } from 'pwa-helpers';
import { css, html, nothing } from "lit";
import { repeat } from 'lit/directives/repeat.js';
import { customElement, property, state } from 'lit/decorators.js';
import store from "../../store/store";
import '../product-card/product-card-display';
import debounce from 'lodash/debounce';
import { BaseComponent } from '../base-component/base-component';
import { booleanConverter } from '../../common/converters';
import { StoreState } from '../../store/store.interface';
import { Modal } from '../modal/modal';
import emit from '../../common/events';
import { getUserDataForUserAnalytics } from '../user-profile/user-profile.selectors';
import { searchParam, selectFacets, selectRequestFacetValues, selectTotal, selectV2Request, selectedFacetCount, selectFacetLabels } from './store/search-v2.selectors';
import { getSearchV2Request, performV2Search } from './thunks/perform-search-v2.thunk';
import { facetsMapping } from '../../common/utils/search-v2-util';
import { FacetLabels, FacetLabelsEnum } from '../tabbed-search/tabbed-search-result/interface/tabbed-search.interface';
import { setLoading } from './store/search-v2.store';
import { SearchV2Facet, SearchV2FacetValue } from './interface/search-v2.interface';

/**
 * The default order configuration array for search filters facets.
 */
export const DEFAULT_FACETS_ORDER: Array<string> = ['TYPE', 'PRODUCT_TAG_PATH', 'PRODUCT_SCHOOL_LEVEL_PATH', 'PRODUCT_CURRICULUM_FOCUS_PATH', 'PROGRAM_TITLE', 'PRODUCT_TYPE_PATH', 'PRODUCT_FORMAT', 'PRODUCT_COPYRIGHT_YEAR', 'SPECIFIC_MARKET'];

/**
 * An element to display search filters
 *
 * @csspart group
 * @csspart subgroup
 * @csspart header
 * @csspart subheader
 * 
 * @event filter-clear-all - A user has clicked the remove all filters button.
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: n/a
 * @event filter-add-facet - A user has added a filter
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { group, name, label }
 * @event filter-remove-facet - A user has removed a filter
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { group, name, label }
 * @event filter-remove-facet-pill - A user has removed a filter
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { group, name, label }
 * @event filter-open-modal - A user has opened filter modal
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: n/a
 * @event filter-close-modal - A user has closed filter modal
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: n/a
 */
@customElement('mhecomm-search-v2-filters')
export class SearchFilters extends connect(store)(BaseComponent) {
  static styles = css`
    .dropdown-item:hover, .dropdown-item:focus, .dropdown-item.bg-light {
      font-weight: bold;
    }
  `;

  @state()
  private facets?: Array<SearchV2Facet>;

  @state()
  private facetLabels?: FacetLabels;

  private readonly FACET_TO_STORE_KEY_MAPPING: Record<string, string> = {
    'learning_level': FacetLabelsEnum.BUSINESS_UNIT,
    'user_type': FacetLabelsEnum.PERSONAS,
    'product': FacetLabelsEnum.PROGRAMS_SOLUTIONS,
    'created_by_division': FacetLabelsEnum.CREATED_BY_DIVISION,
  };

  @property()
  store?: string;

  /**
   * is plp search within or not
   */
  @property({ converter: booleanConverter })
  plpSearch = false;

  @property()
  categoryId: string | undefined;

  @property({ converter: booleanConverter })
  tabbedSearchEnabled = false;

  @state()
  private showAriaInModal = false;

  @state()
  private displayProgramsLimit = 10;

  @state()
  private filterModal;

  @state()
  private lastUpdatedFacet: string | null = null;

  private selectedFacetCount;
  private bs5ModalInstance;
  private isFilterRenderedInModal = false;
  private isModalVisible = false;
  private appliedFilter: Array<SearchV2Facet> = [];
  private totalSearchResult = 0;

  private readonly onResize = debounce(() => this.udpateFilterPlacement(), 300);

  renderComp() {
    return html`
      <div tabindex="-1" role="region" aria-label="Filters. Selecting or deselecting a filter will update results and may change the available options in other filters.">
        <span id="region-filter-v2-announcements" aria-live="polite" class="visually-hidden"></span>
        ${this.isFilterRenderedInModal && this.facets?.length ?
        this.renderFilterModal() :
        html`
            ${this.appliedFilter.length ? this.renderAppliedFilter() : nothing}
            ${this.facets?.length ? this.renderFacetHeader() : nothing}
            ${this.renderFacets()}
          `
      }
      </div>
    `;
  }

  renderFacetHeader() {
    return html`
      <div class="d-flex d-md-block d-lg-flex justify-content-between align-items-end mb-2">
        <div class="d-flex justify-content-between w-100">
          <h2 class="h6 mb-0 me-2 fw-bold lh-base">Filter By</h2>
          ${this.appliedFilter?.length > 0
        ? nothing
        : this.renderSkipFIlter()}
        </div>
      </div>
    `;
  }

  renderSkipFIlter() {
    if (this.tabbedSearchEnabled) {
      return html`
        <button
          id="skip-to-results-tab"
          data-automation-id="skip-to-results-tab"
          class="visually-hidden-focusable btn btn-link p-0 border-0"
          @click=${this.skipFilterToTabClick}
        >
          Skip to Results Tab
        </button>
      `;
    }

    return html`
      <button 
        id="skip-to-results" 
        data-automation-id="skip-to-results" 
        class="visually-hidden-focusable btn btn-link" 
        @click=${this.skipFilterClick}> 
          Skip to Results 
      </button>
    `;
  }

  renderFilterModal() {
    const modalContextId = 'filter';
    this.filterModal = new Modal({
      modalContext: {
        componentRef: modalContextId,
        componentRefData: this.analyticDetail(),
      },
      header: 'Filter By',
      body: {
        template: html`
          ${this.showAriaInModal
            ? html`<div class="visually-hidden" aria-live="polite" role="status">
                ${this.totalSearchResult} Results
              </div>`
            : nothing}
          ${this.appliedFilter.length ? this.renderAppliedFilter() : nothing} ${this.renderFacets()}
        `,
      },
      disableFooter: true,
    });

    this.filterModal.addEventListener(`${modalContextId}-modal-close`, () => {
      (this.shadowRoot?.querySelector('.filter-by-button') as HTMLInputElement)?.focus();
    });

    return html`
      <div class="text-center">
        <button 
          type="button" 
          class="btn btn-outline-primary w-100 filter-by-button" 
          data-bs-toggle="modal" 
          data-bs-target="#filterModal"
          aria-label="Open filters. ${this.selectedFacetCount ? `${this.selectedFacetCount} filters currently selected.` : ''}"
          @click=${this.openModal}
        >
          Filter By ${this.selectedFacetCount ? `(${this.selectedFacetCount})` : ''}
        </button>
      </div>
    `;
  }

  renderFacets() {
    // Only render facets that have facetValues
    const validFacets = this.facets?.filter(facet => facet.facetValues && facet.facetValues.length > 0) || [];
    
    return html`
        ${validFacets?.map((facet, index) => html`
            <div class="accordion mb-3 ${this.isFilterRenderedInModal ? 'accordion-flush' : ''}" id="${facet.name}-accordion" data-automation-id="${facet.name}-accordion">
              <div class="accordion-item ${this.isFilterRenderedInModal ? 'border-primary' : ''}">
                <div class="${this.isFilterRenderedInModal ? 'border-bottom' : ''}">  
                  <h3 class="accordion-header" id="${facet.name}">
                    <button class="accordion-button fw-semibold mb-0 shadow-none text-capitalize ${this.isFilterRenderedInModal ? "px-0 mb-0" : ''}" type="button" data-bs-toggle="collapse" data-bs-target="#${facet.name}-collapse-${index}" aria-expanded="true" aria-controls="${facet.name}-collapse-${index}" @click=${(e) => this._handleCollapseToggle(e, facet.name)}>
                      ${facet.label}
                    </button>
                  </h3>
                  <div id="${facet.name}-collapse-${index}" class="accordion-collapse collapse show ${this.isFilterRenderedInModal ? "px-0 py-2" : 'p-3'}" aria-labelledby="${facet.name}" role="region" data-bs-parent="#${facet.name}-accordion">
                    <fieldset>
                      <legend class="visually-hidden">${facet.label} checkbox</legend>
                       ${this.lastUpdatedFacet === facet.name ? html`
                      <span 
                        role="status" 
                        aria-live="polite" 
                        aria-atomic="true" 
                        class="visually-hidden">
                        Filters updated. ${facet.label}: ${facet.facetValues.length} filters available.
                      </span>
                    ` : nothing}
                    ${repeat(facet.facetValues, facetValue => facetValue.name, ((facetValue, facetIndex) => {
      let collapseClass = '';
      if (facetIndex >= this.displayProgramsLimit) {
        collapseClass = `collapse-facet-programs-${facet.name} collapse`;
      }
      return this.renderCheckbox(facet, facetValue, collapseClass);
    }))}
                    </fieldset>
                    ${facet.facetValues.length > this.displayProgramsLimit
        ? html` <a
                          class="text-decoration-none"
                          data-bs-target="viewAllFacetPrograms-${facet.name}"
                          data-bs-toggle="collapse"
                          aria-controls="viewAllFacetPrograms-${facet.name}"
                          href="javascript:;"
                          aria-expanded="false"
                          id="viewAllFacetProgramsLink-${facet.name}"
                          @click=${(event) => this._handleFacetProgramsCollapseToggle(facet, facet.facetValues, event)}
                          >View All
                          ${this.isFilterRenderedInModal
            ? html`<uxt-icon
                                class="px-2"
                                id="enterDropDownIcon-${facet.name}"
                                icon="chevron-down"
                              ></uxt-icon>` : nothing}
                          </a>`
        : nothing}
                  </div>
                </div>
              </div>
            </div>
          `)
      }
    `;
  }

  /**
   * Gets the display label for a facet value with intelligent fallback.
   * 
   * Fallback priority:
   * 1. Custom label from Redux store (facetLabels)
   * 2. Static label from facetsMapping utility
   * 3. Original label from API response
   */
  private getFacetValueLabel(facetName: string, valueName: string, defaultLabel: string): string {
    const storeKey = this.FACET_TO_STORE_KEY_MAPPING[facetName];

    if (storeKey && this.facetLabels?.[storeKey]?.[valueName]) {
      return this.facetLabels[storeKey][valueName];
    }
    
    if (facetsMapping[valueName]) {
      return facetsMapping[valueName];
    }
    
    return defaultLabel;
  }

  renderCheckbox(facet, value, collapseClass = '', isPerformSearch = true) {
    return html`
      <div class="flex-row pb-2 pe-3 ${collapseClass}">
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            .checked=${value.selected}
            value=${value.label}
            data-automation-id="${this.replaceSpaceWithinStr(value.name)}"
            id="${this.replaceSpaceWithinStr(value.name)}"
            @change=${(e: Event) => {
              const checkbox = e.target as HTMLInputElement;
              if (!isPerformSearch) {
                const facetValues = selectRequestFacetValues(store.getState() as StoreState, facet.name);
                if (facetValues) {
                  value = { ...value, selected: facetValues.includes(value.name) };
                }
              }

              const announcer = this.shadowRoot?.getElementById('region-filter-v2-announcements');
              if (announcer) {
                const action = checkbox.checked ? 'added' : 'removed';
                announcer.textContent = `${value.label} filter ${action}. Search results have been updated.`;
              }

              this.updateFacet(facet.name, value, false, isPerformSearch);
      }}
          />
          <label class="form-check-label d-flex justify-content-between" for="${this.replaceSpaceWithinStr(value.name)}">
            <span> ${this.getFacetValueLabel(facet.name, value.name, value.label)} </span> <span>(${value.count})</span>
          </label>
        </div>
      </div>
    `;
  }

  private _handleFacetProgramsCollapseToggle(facet, facetValues, event) {
    if (event && (event.type == 'click' || (event.type == 'keyup' && event.code == 'Space'))) {
      if (this.isFilterRenderedInModal) {
        const viewAllFacetProgramsLink = this.shadowRoot!.querySelector(`#viewAllFacetProgramsLink-${facet.name}`) as HTMLLinkElement;
        const dropDownIcon = this.shadowRoot!.querySelector(`#enterDropDownIcon-${facet.name}`);
        const collapsePrograms = this.shadowRoot!.querySelectorAll(`.collapse-facet-programs-${facet.name}`);
        const collapseProgramClass = `flex-row pb-2 pe-3 collapse-facet-programs-${facet.name} collapse`;
        if (viewAllFacetProgramsLink.getAttribute('aria-expanded') == 'true') {
          viewAllFacetProgramsLink!.firstChild!.textContent = 'View All';
          viewAllFacetProgramsLink.setAttribute('aria-expanded', 'false');
          dropDownIcon?.setAttribute('icon', 'chevron-down');
          collapsePrograms?.forEach((element) => element.setAttribute('class', `${collapseProgramClass}`));
        } else {
          viewAllFacetProgramsLink!.firstChild!.textContent = 'View Less';
          viewAllFacetProgramsLink.setAttribute('aria-expanded', 'true');
          dropDownIcon?.setAttribute('icon', 'chevron-up');
          collapsePrograms?.forEach((element) => element.setAttribute('class', `${collapseProgramClass} show`));
        }
      } else {
        const modal: Modal = new Modal({
          header: facet.label,
          isHeaderTextCapitalize: true,
          body: {template: this._getFacetProgramsTemplate(facet, facetValues)},
          primaryButton: {label: 'Apply'},
          modalContext: { componentRef: `view-all-facet-programs` },
        });
        modal.addEventListener('mhecomm-modal-confirmed', () => {
          store.dispatch(performV2Search());
          modal.close();
        });
        modal.open();
      }
    }
  }

  private _getFacetProgramsTemplate(facet, facetValues) {
    return html`
      <style>#allPrograms {max-height: 60vh;}</style>
      <div id="allPrograms" class="overflow-auto ps-1">
        ${facetValues?.map((facetValue) => this.renderCheckbox(facet, facetValue, '', false))}
      </div>
    `;
  }

  renderAppliedFilter() {
    return html`
        <div 
          class="d-flex justify-content-between w-100"
        >
          <h2 class="h6 fw-bold mb-3">Applied Filters</h2>
          ${this.isFilterRenderedInModal ? nothing : html`
              <div>${this.renderSkipFIlter()}</div>
            `
      }
        </div>
        ${this.appliedFilter.map((facet) => html`
          <div class="mb-3">
            <h3 class="h6 fw-normal">${facet.label}:</h3>
            <ul class="list-unstyled">
              ${facet.facetValues?.map((facetValue) => html`
                  <li>
                    <div
                      class="alert alert-secondary alert-dismissible mb-2 py-2 ps-2 d-flex align-items-top"
                    >
                      <div>
                        <p class="mb-0">${this.getFacetValueLabel(facet.name, facetValue.name, facetValue.label)}</p>
                        <button
                          data-automation-id="${this.replaceSpaceWithinStr(facetValue.name)}_clear" 
                          id="${this.replaceSpaceWithinStr(facetValue.name)}_clear"
                          type="button"
                          class="btn-close btn-sm pb-2"
                          data-bs-dismiss="alert"
                          aria-label="Remove Filter ${facetValue.label}"
                          @click=${() =>{
                            const announcer = this.shadowRoot?.getElementById('region-filter-v2-announcements');
                            if (announcer) {
                              announcer.textContent = `${facetValue.label} filter removed. Search results have been updated.`;
                            }
                            this.updateFacet(facet.name, facetValue, true); 
                          } 
                        }
                        ></button>
                      </div>
                    </div>
                    </li>
                  </div>
                `)
        }
            </ul>
          </div>`)
      }
        ${this.selectedFacetCount ? html`
            <button class="btn btn-link p-0 mb-3" 
              @click=${() => {
                const announcer = this.shadowRoot?.getElementById('region-filter-v2-announcements');
                if (announcer) {
                  announcer.textContent = 'All filters cleared. Search results have been updated.';
                }
              this.clearAllFilters();
            }}
              aria-label="Clear All Filters"
              id="clear_filters"
              data-automation-id="clear_filters">
              Clear Filters
            </button>
        ` : nothing
      }
    `;
  }

  _handleCollapseToggle(e: PointerEvent, name: string) {
    const element = e.target as HTMLElement;
    const collaseId = element.getAttribute('data-bs-target') as string;
    const collapseInstance = this.bsInit(collaseId, window.bootstrap?.Collapse, { toggle: false })?.instance;
    const collapse = (this.shadowRoot?.querySelector(collaseId) as HTMLElement);
    if (element.classList.contains('collapsed')) {
      collapse.addEventListener('show.bs.collapse', () => element.classList.remove('collapsed'), { once: true })
      collapseInstance.show();
      emit({
        element: this,
        name: 'filter-group-open',
        detail: this.analyticDetail({ name }),
        dataLayer: true
      });
      element.setAttribute("aria-expanded", "true");
    } else {
      collapse.addEventListener('hide.bs.collapse', () => element.classList.add('collapsed'), { once: true })
      collapseInstance.hide();
      emit({
        element: this,
        name: 'filter-group-close',
        detail: this.analyticDetail({ name }),
        dataLayer: true
      });
      element.setAttribute("aria-expanded", "false");
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addTrackedEventListener(window, 'resize', this.onResize);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
  }

  updated() {
    this.udpateFilterPlacement();
    this.bs5ModalInstance = this.bsInit('#filterModal', window.bootstrap?.Modal)?.instance;
  }

  udpateFilterPlacement() {
    const windowWidth = () => {
      const clientWidth = window.document.documentElement.clientWidth;
      const body = window.document.body;
      return window.document.compatMode === "CSS1Compat" && clientWidth || body && body.clientWidth || clientWidth;
    }

    const bool = this.isFilterRenderedInModal;

    if (windowWidth() >= 768)
      this.isFilterRenderedInModal = false;
    else
      this.isFilterRenderedInModal = true;

    if (bool != this.isFilterRenderedInModal)
      this.requestUpdate();
  }

  stateChanged(state: StoreState) {
    this.facets = selectFacets(state);
    this.facets = this.facets?.filter((facet, _i) => facet.label !== 'Doc_type');
    this.facetLabels = selectFacetLabels(state);
    // if(selectSelectedCatalog(state) === Catalog.seg && this.facets?.length) {
    //   this.facets.sort((a, b) => DEFAULT_FACETS_ORDER.indexOf(a.name) - DEFAULT_FACETS_ORDER.indexOf(b.name));
    //   this.updateFacetLabel();
    // }
    this.selectedFacetCount = selectedFacetCount(state);
    this.totalSearchResult = selectTotal(state) || 0;
    this.appliedFilter = this.facets?.reduce((arr: any, facet) => {
      const selectedFacetValues = facet?.facetValues.filter(facetValue => facetValue.selected);
      if (selectedFacetValues.length) {
        arr.push({
          ...facet,
          facetValues: selectedFacetValues
        });
      }
      return arr;
    }, []) || [];
  }

  /**
   * in this function change facet lebel “Disciplines” to “Subject” for PreK-12 by checking facet code
   */
  private updateFacetLabel() {
    const updatedFacets = this.facets?.map((facet) => {
      if (facet.name === 'PRODUCT_TAG_PATH') {
        return { ...facet, label: 'Subject' };
      }
      return facet;
    });
    this.facets = updatedFacets;
  }

  openModal() {
    emit({
      element: this,
      name: 'filter-open-modal',
      detail: this.analyticDetail(),
    });
    
    // Use the Modal instance directly if available
    if (this.filterModal && typeof this.filterModal.open === 'function') {
      this.filterModal.open();
      this.isModalVisible = true;
      return;
    }
    
    // Try using the Bootstrap modal instance as fallback
    try {
      if (!this.bs5ModalInstance) {
        this.bs5ModalInstance = this.bsInit('#filterModal', window.bootstrap?.Modal, { backdrop: true, keyboard: true })?.instance;
      }
      
      if (this.bs5ModalInstance) {
        this.bs5ModalInstance.show();
        this.isModalVisible = true;
      } else {
        console.warn('Bootstrap Modal instance not available');
        // Create a new modal instance as a last resort
        if (!this.filterModal) {
          this.renderFilterModal();
        }
        if (this.filterModal && typeof this.filterModal.open === 'function') {
          this.filterModal.open();
          this.isModalVisible = true;
        } else {
          console.error('Failed to create or open modal');
        }
      }
    } catch (err) {
      console.error('Error opening modal:', err);
    }
  }

  closeModal() {
    emit({
      element: this,
      name: 'filter-close-modal',
      detail: this.analyticDetail(),
    });
    this.bs5ModalInstance.hide();
    this.isModalVisible = false;
  }

  clearAllFilters() {
    const state = store.getState() as StoreState;
    const searchString: string = searchParam(state)!;
    const request = selectV2Request(state);
    store.dispatch(performV2Search({
      fieldValueFilter: {},
      searchTerms: searchString,
      start: 1,
      pageLength: request?.pageLength
    }));

    emit({
      element: this,
      name: 'filter-clear-all',
      detail: this.analyticDetail(),
      dataLayer: true
    });
    this.lastUpdatedFacet = null;
  }

  clearFilterAndClose() {
    this.clearAllFilters();
    this.closeModal();
  }

  updateFacet(facet: string, value: SearchV2FacetValue, viaPill = false, isPerformSearch = true) {
    const state = store.getState() as StoreState;
    const searchString: string = searchParam(state)!;
    const val = value;
    let params = {};
    if (val.selected) {
      params = {
        removeFacetName: facet,
        removeFacetValue: val.name,
        excludeFacetName: facet,
        isPlpSearch: this.plpSearch,
        searchTerms: searchString,
        start: 1
      };
    } else {
      params = {
        addFacetName: facet,
        addFacetValue: val.name,
        excludeFacetName: facet,
        isPlpSearch: this.plpSearch,
        searchTerms: searchString,
        start: 1,
        categoryId: this.categoryId
      };
    }

    if (isPerformSearch) {
      store.dispatch(performV2Search(params));
    } else {
      store.dispatch(setLoading(getSearchV2Request(params, state)));
    }

    let analyticLogName = value.selected ? 'filter-remove-facet' : 'filter-add-facet';
    if (viaPill) {
      analyticLogName = 'filter-remove-facet-pill';
    }

    // Prepare the array of applied filters
    const appliedFilters = this.appliedFilter.map((filter) => ({
      group: filter.name,
      label: filter.label,
    }));

    emit({
      element: this,
      name: analyticLogName,
      detail: {
        ...this.analyticDetail({ group: facet, name: value.name, label: value.label }),
        'Refinement Type': 'Filter By', // Add Refinement Type
        appliedFilters, // Add applied filters to the event detail
      },
      dataLayer: true
    });

    this.showAriaInModal = true;
    this.lastUpdatedFacet = facet;
  }

  analyticDetail(obj = {}) {
    const state = store.getState() as StoreState;
    return {
      id: this.id,
      store: this.store,
      user: getUserDataForUserAnalytics(state),
      catalog: 'SUPPORT',
      ...obj
    };
  }

  replaceSpaceWithinStr(val: string) {
    return val.replace(/\s/g, '_')
  }

  /** Set focus on results bar when skip filters is clicked or pressed */
  skipFilterClick(e) {
    emit({
      element: this,
      name: 'skip-to-results',
      detail: {
        target: 'results-info-bar'
      },
      dataLayer: true
    });
    e.target.blur();
  }

  skipFilterToTabClick(e) {
    emit({
      element: this,
      name: 'skip-to-results-tab',
      detail: {
        target: 'results-tab'
      },
      dataLayer: true
    });
    e.target.blur();
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-search-v2-filters': SearchFilters;
  }
}
